import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'
import {
  AlertCircle,
  GitMerge,
  Lock,
  MoreHorizontal,
  Pencil,
  Trash2,
  Unlink,
  X,
} from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useProductDetail } from '../hooks/use-product-detail'
import { VAT_CODE_OPTIONS, buildUpdateProductSchema } from '../types'
import { MergeProductDialog } from './merge-product-dialog'
import { DeleteProductDialog } from './delete-product-dialog'
import { ProductPackageUnitsSection } from './product-package-units-section'
import type { UpdateProductFormData } from '../types'
import type { UpdateProductRequest } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { productsApi } from '@/api/endpoints/products'
import { externalRefsApi } from '@/api/endpoints/external-refs'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { ApiError } from '@/api/types'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import {
  formatShare,
  formatStockQuantity,
  parseStockQuantity,
} from '@/shared/lib/stock-quantity'

interface ProductInfoTabProps {
  sku: string
}

function FieldRow({
  label,
  children,
  mono,
  inherited,
}: {
  label: string
  children: React.ReactNode
  mono?: boolean
  /** CHG-2026-09-stock-usage-and-price-twins: a variant's field that comes from its source. */
  inherited?: string
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-baseline gap-x-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className={`text-sm ${mono ? 'font-mono' : ''}`}>
        {children}
        {inherited && (
          <span className="ml-2 font-sans text-xs text-muted-foreground">
            {inherited}
          </span>
        )}
      </dd>
    </div>
  )
}

export function ProductInfoTab({ sku }: ProductInfoTabProps) {
  const queryClient = useQueryClient()
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const [isEditing, setIsEditing] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [unmergeOpen, setUnmergeOpen] = useState(false)
  const [shareInput, setShareInput] = useState('')
  // CHG-2026-09-stock-usage-and-price-twins: a variant's share input; 'ambiguousDot' gets its hint.
  const [shareError, setShareError] = useState<
    false | 'invalid' | 'ambiguousDot'
  >(false)
  const { data: product, isLoading, error } = useProductDetail(sku)
  const importedQuery = useQuery({
    queryKey: ['product-external-ref-webbill', 'list'],
    queryFn: () => externalRefsApi.listBySource('webbill'),
    staleTime: 30_000,
  })
  const isImported = !!importedQuery.data?.items.some(
    (it: any) => it.sku === sku,
  )
  const { can } = usePermissions()
  // CHG-2026-09-duplicate-product-merge: a merged duplicate keeps edit/price/menu/assignment
  // actions disabled — it stays in the catalog only as a redirected, frozen row.
  const isMerged = !!product?.mergedIntoSku
  const duplicateSkus = product?.duplicateSkus ?? []
  const canEdit = can(ACTIONS.PRODUCT_EDIT) && !isMerged
  const canDelete = can(ACTIONS.PRODUCT_DELETE)
  const canMerge = can(ACTIONS.PRODUCT_EDIT) && isImported
  // CHG-2026-09-stock-usage-and-price-twins (10.6): a variant owns its names, share and status;
  // VAT rate, warm/kalt and category come from its source, and it never has a barcode.
  const isVariant = !!product?.variantSourceSku
  const isStockArticle = !!product && !isVariant && !product.isDish

  const unmergeMutation = useMutation({
    mutationFn: () => productsApi.unmerge(sku),
    onSuccess: () => {
      toast.success(tProducts('merge.undoSuccess'))
      queryClient.invalidateQueries({ queryKey: ['products', sku] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setUnmergeOpen(false)
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(getErrorMessage(err.status, err.code))
      } else {
        toast.error(tProducts('merge.undoFailed'))
      }
    },
  })

  const vatLabel =
    VAT_CODE_OPTIONS.find((o) => o.value === product?.vatCode)?.label ??
    product?.vatCode

  const updateProductSchema = useMemo(
    () => buildUpdateProductSchema(tProducts),
    [tProducts],
  )

  const form = useForm<UpdateProductFormData>({
    resolver: zodResolver(updateProductSchema),
    values: product
      ? {
          name: product.name,
          publicName: product.publicName ?? '',
          vatCode: product.vatCode,
          active: product.active,
          temperatureChoice: product.temperatureChoice ?? true,
          barcode: product.barcode ?? '',
        }
      : undefined,
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateProductRequest) => productsApi.update(sku, data),
    onSuccess: () => {
      toast.success(tProducts('toast.updateSuccess'))
      queryClient.invalidateQueries({ queryKey: ['products', sku] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setIsEditing(false)
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(getErrorMessage(err.status, err.code))
      } else {
        toast.error(tProducts('toast.updateFailed'))
      }
    },
  })

  const onSubmit = (data: UpdateProductFormData) => {
    const payload: UpdateProductRequest = { ...data }
    if (isVariant) {
      // Inherited fields are never sent for a variant: the server refuses a VAT rate or warm/kalt
      // of its own (409 product_is_variant), and a variant has no barcode.
      delete payload.vatCode
      delete payload.temperatureChoice
      delete payload.barcode
      const parsed = parseStockQuantity(shareInput)
      if (!parsed.ok) {
        setShareError(
          parsed.error === 'ambiguousDot' ? 'ambiguousDot' : 'invalid',
        )
        return
      }
      if (parsed.value !== product.variantShare) payload.share = parsed.value
    }
    if (isImported) {
      delete payload.name
      delete payload.publicName
      delete payload.vatCode
    }
    if (
      payload.publicName !== undefined &&
      !String(payload.publicName).trim()
    ) {
      delete payload.publicName
    }
    if (payload.barcode !== undefined && !String(payload.barcode).trim()) {
      delete payload.barcode
    }
    updateMutation.mutate(payload)
  }

  const handleCancelEdit = () => {
    form.reset()
    setShareError(false)
    setIsEditing(false)
  }

  const startEditing = () => {
    setShareInput(formatStockQuantity(product?.variantShare ?? 1))
    setShareError(false)
    setIsEditing(true)
  }

  const fromSource = isVariant ? tProducts('variant.fromSource') : undefined

  const emptyValue = (
    <span className="text-muted-foreground">
      {tProducts('info.emptyValue')}
    </span>
  )

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-5 w-1/4" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tCommon('error')}</AlertTitle>
        <AlertDescription>
          {tProducts('info.loadErrorDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  if (!product) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tCommon('notFound')}</AlertTitle>
        <AlertDescription>
          {tProducts('info.notFoundDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  if (!isEditing) {
    return (
      <div className="space-y-5">
        {isImported && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950">
            <Lock className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {tProducts('info.importedViewOnly')}
            </p>
          </div>
        )}

        {isMerged && (
          <div className="flex flex-col gap-1 rounded-md border border-warning/50 bg-warning/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <GitMerge className="h-4 w-4 shrink-0 text-warning" />
              <p className="text-sm text-warning">
                {tProducts('merge.badgeMergedShort')}:{' '}
                <Link
                  to={`/products/$sku` as '/products/$sku'}
                  params={{ sku: product.mergedIntoSku! }}
                  className="underline font-medium"
                >
                  {product.mergedIntoSku}
                </Link>
              </p>
            </div>
            <p className="text-xs text-warning/80">
              {tProducts('merge.disabledNotice')}
            </p>
          </div>
        )}

        {duplicateSkus.length > 0 && (
          <div className="rounded-md border px-3 py-2">
            <p className="text-sm font-medium mb-1">
              {tProducts('merge.duplicatesTitle')}
            </p>
            <p className="text-xs text-muted-foreground mb-2">
              {tProducts('merge.duplicatesDescription')}
            </p>
            <div className="flex flex-wrap gap-2">
              {duplicateSkus.map((dupSku) => (
                <Link
                  key={dupSku}
                  to={`/products/$sku` as '/products/$sku'}
                  params={{ sku: dupSku }}
                >
                  <Badge variant="outline" className="font-mono">
                    {dupSku}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-start">
          <dl className="space-y-3 flex-1">
            <FieldRow label={tProducts('fields.productName')}>
              {product.name}
            </FieldRow>
            <FieldRow label={tProducts('fields.menuName')}>
              {product.publicName ?? emptyValue}
            </FieldRow>
            {isVariant ? (
              <>
                <FieldRow label={tProducts('variant.share')}>
                  {formatShare(product.variantShare ?? 1)}{' '}
                  <span className="text-xs text-muted-foreground">
                    {tProducts('variant.shareUsage', {
                      share: formatStockQuantity(product.variantShare ?? 1),
                    })}
                  </span>
                </FieldRow>
                <FieldRow label={tProducts('fields.barcode')}>
                  <span className="text-muted-foreground">
                    {tProducts('variant.noBarcode')}
                  </span>
                </FieldRow>
              </>
            ) : (
              <FieldRow label={tProducts('fields.barcode')} mono>
                {product.barcode ?? emptyValue}
              </FieldRow>
            )}

            <Separator />

            <FieldRow
              label={tProducts('fields.vatCode')}
              inherited={fromSource}
            >
              {vatLabel}
            </FieldRow>
            <FieldRow label={tCommon('status')}>
              <span
                className={
                  product.active
                    ? 'text-green-700 dark:text-green-400'
                    : 'text-muted-foreground'
                }
              >
                {product.active ? tCommon('active') : tCommon('inactive')}
              </span>
              {!product.active && product.pausedBySource && (
                <span className="ml-2 text-xs text-muted-foreground">
                  {tProducts('variant.paused')}
                </span>
              )}
            </FieldRow>
            <FieldRow
              label={tProducts('fields.temperatureChoice')}
              inherited={fromSource}
            >
              {(product.temperatureChoice ?? true)
                ? tCommon('yes')
                : tCommon('no')}
            </FieldRow>
          </dl>

          {(canEdit || canDelete || canMerge) && (
            <div className="flex items-center gap-2">
              {canEdit && (
                <Button variant="outline" size="sm" onClick={startEditing}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {tCommon('edit')}
                </Button>
              )}

              {(canDelete || canMerge) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canMerge && !isMerged && (
                      <DropdownMenuItem
                        onClick={() => setMergeDialogOpen(true)}
                      >
                        <GitMerge className="h-4 w-4 mr-2" />
                        {tProducts('merge.action')}
                      </DropdownMenuItem>
                    )}
                    {canMerge && isMerged && (
                      <DropdownMenuItem onClick={() => setUnmergeOpen(true)}>
                        <Unlink className="h-4 w-4 mr-2" />
                        {tProducts('merge.undoAction')}
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => setDeleteOpen(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {tCommon('delete')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>

        <MergeProductDialog
          sku={sku}
          open={mergeDialogOpen}
          onOpenChange={setMergeDialogOpen}
        />

        <AlertDialog open={unmergeOpen} onOpenChange={setUnmergeOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {tProducts('merge.undoTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {tProducts('merge.undoDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => unmergeMutation.mutate()}
                disabled={unmergeMutation.isPending}
              >
                {tProducts('merge.undoConfirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <DeleteProductDialog
          sku={sku}
          productName={product.name}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
        />

        {isStockArticle && (
          <ProductPackageUnitsSection
            key={product.stockUnitLabel ?? ''}
            product={product}
            canEdit={canEdit}
          />
        )}
      </div>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        {isImported && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950">
            <Lock className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {tProducts('info.importedEditOnlyStatus')}
            </p>
          </div>
        )}

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tProducts('fields.productName')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={updateMutation.isPending || isImported || !canEdit}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="publicName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tProducts('fields.menuName')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={updateMutation.isPending || isImported || !canEdit}
                  placeholder={tProducts('form.menuNamePlaceholderEdit')}
                />
              </FormControl>
              <FormDescription>
                {tProducts('form.menuNameDescriptionEdit')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {isVariant && (
          <div className="space-y-2">
            <Label htmlFor="variant-share">{tProducts('variant.share')}</Label>
            <Input
              id="variant-share"
              value={shareInput}
              onChange={(e) => {
                setShareInput(e.target.value)
                setShareError(false)
              }}
              inputMode="decimal"
              className="max-w-[160px] text-right"
              disabled={updateMutation.isPending || !canEdit}
              aria-invalid={shareError ? true : undefined}
            />
            <p
              className={`text-sm ${shareError ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {shareError === 'ambiguousDot'
                ? tProducts('stockUsage.partError.ambiguousDot')
                : shareError
                  ? tProducts('variant.shareInvalid')
                  : tProducts('variant.shareEditHint')}
            </p>
          </div>
        )}

        {isVariant ? (
          <dl className="space-y-3 rounded-md border bg-muted/40 p-4">
            <FieldRow label={tProducts('fields.barcode')}>
              <span className="text-muted-foreground">
                {tProducts('variant.noBarcode')}
              </span>
            </FieldRow>
            <FieldRow
              label={tProducts('fields.vatCode')}
              inherited={fromSource}
            >
              {vatLabel}
            </FieldRow>
            <FieldRow
              label={tProducts('fields.temperatureChoice')}
              inherited={fromSource}
            >
              {(product.temperatureChoice ?? true)
                ? tCommon('yes')
                : tCommon('no')}
            </FieldRow>
          </dl>
        ) : (
          <FormField
            control={form.control}
            name="barcode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tProducts('fields.barcode')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={updateMutation.isPending || !canEdit}
                    placeholder={tProducts('form.barcodePlaceholderEdit')}
                  />
                </FormControl>
                <FormDescription>
                  {tProducts('form.barcodeDescriptionEdit')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <Separator />

        {!isVariant && (
          <FormField
            control={form.control}
            name="vatCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tProducts('fields.vatCode')}</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={updateMutation.isPending || isImported || !canEdit}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={tProducts('form.vatCodePlaceholder')}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {VAT_CODE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  {tProducts('form.vatCodeDescription')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={updateMutation.isPending || !canEdit}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>{tCommon('active')}</FormLabel>
                <FormDescription>
                  {tProducts('form.activeDescription')}
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        {!isVariant && (
          <FormField
            control={form.control}
            name="temperatureChoice"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={updateMutation.isPending || !canEdit}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>{tProducts('fields.temperatureChoice')}</FormLabel>
                  <FormDescription>
                    {tProducts('form.temperatureChoiceDescription')}
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancelEdit}
            disabled={updateMutation.isPending}
          >
            <X className="h-4 w-4 mr-2" />
            {tCommon('cancel')}
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={updateMutation.isPending || !canEdit}
          >
            {updateMutation.isPending
              ? tCommon('saving')
              : tCommon('saveChanges')}
          </Button>
        </div>
      </form>
    </Form>
  )
}
